import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { Minus, Send, MessageCircle, Phone, Paperclip, X as XIcon, CornerDownRight } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useRealtimeEvents } from "@/hooks/useRealtimeEvents";
import { ChatMessage } from "@/components/chat/ChatMessage";
import { VoiceRecorder, blobToBase64 } from "@/components/chat/VoiceRecorder";
import { Avatar } from "@/components/Avatar";
import { EmojiButton, StickerButton } from "@/components/chat/EmojiPicker";
import { DictateButton } from "@/components/chat/DictateButton";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STATE_KEY = "ca_user_chat_state"; // "open" | "min" (no "closed" — user can only minimize)

/**
 * UserChatWidget — Facebook Messenger style floating chat for logged-in users.
 * - Auto-opens for newly-registered users (first login).
 * - Auto-opens when a new admin message arrives.
 * - Users can ONLY minimize (no close button).
 * - Persisted state in localStorage.
 * - Visible on home (/) and /mi-cuenta. Hidden on /lecturaonline and /admin.
 */
export const UserChatWidget = () => {
  const { user, authHeader } = useAuth();
  const location = useLocation();
  const [messages, setMessages] = useState([]);
  const [adminInfo, setAdminInfo] = useState(null);
  const [selfHasPic, setSelfHasPic] = useState(false);
  const [state, setState] = useState(() => {
    try {
      const saved = localStorage.getItem(STATE_KEY);
      // legacy "closed" → upgrade to "min" so the chat is always reachable
      if (saved === "closed" || !saved) return "min";
      return saved;
    } catch { return "min"; }
  });
  const [draft, setDraft] = useState("");
  const [replyingTo, setReplyingTo] = useState(null);
  const [adminTyping, setAdminTyping] = useState(false);
  const typingTimerRef = useRef(null);
  const lastTypingPingRef = useRef(0);
  const [sending, setSending] = useState(false);
  const [unread, setUnread] = useState(0);
  const scrollRef = useRef(null);

  const persist = (s) => {
    setState(s);
    try { localStorage.setItem(STATE_KEY, s); } catch { /* noop */ }
    if (s === "open") setUnread(0);
    window.dispatchEvent(new CustomEvent("ca:chatState", { detail: s }));
  };
  useEffect(() => { window.dispatchEvent(new CustomEvent("ca:chatState", { detail: state })); }, [state]);
  useEffect(() => {
    const h = () => persist("open");
    window.addEventListener("ca:openChat", h);
    return () => window.removeEventListener("ca:openChat", h);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = async () => {
    if (!user) return;
    try {
      const r = await axios.get(`${API}/users/messages`, { headers: authHeader() });
      setMessages(r.data || []);
    } catch { /* ignore */ }
  };

  const markAdminMessagesRead = async () => {
    if (!user) return;
    const unread = messages.filter((m) => m.from_role === "admin" && !m.read_at);
    if (unread.length === 0) return;
    await Promise.all(unread.map((m) =>
      axios.post(`${API}/users/messages/${m.id}/read`, {}, { headers: authHeader() }).catch(() => null)
    ));
  };

  const sendMedia = async ({ blob, mimeType, durationSec, kind }) => {
    if (!blob) return;
    setSending(true);
    try {
      const b64 = await blobToBase64(blob);
      await axios.post(`${API}/users/messages`, {
        body: "", kind, media_b64: b64, media_mime: mimeType, duration_sec: durationSec,
        reply_to: replyingTo?.id || null,
      }, { headers: authHeader() });
      setReplyingTo(null);
      await load();
    } catch (e) {
      alert(e?.response?.data?.detail || "Error enviando media");
    } finally { setSending(false); }
  };

  const sendSticker = async (stickerId) => {
    setSending(true);
    try {
      await axios.post(`${API}/users/messages`, {
        body: "", kind: "sticker", sticker: stickerId, reply_to: replyingTo?.id || null,
      }, { headers: authHeader() });
      setReplyingTo(null);
      await load();
    } catch { /* ignore */ } finally { setSending(false); }
  };

  // Auto-open for new users on first login (no prior chat state)
  useEffect(() => {
    if (!user) return;
    load();
    // Fetch admin's avatar info + my own avatar status
    axios.get(`${API}/admin/public-info`, { headers: authHeader() })
      .then((r) => setAdminInfo(r.data))
      .catch(() => null);
    axios.get(`${API}/users/me/extra`, { headers: authHeader() })
      .then((r) => setSelfHasPic(!!r.data.has_profile_pic))
      .catch(() => null);
    try {
      const seen = localStorage.getItem(`ca_user_chat_seen_${user.id}`);
      if (!seen) {
        persist("open");
        localStorage.setItem(`ca_user_chat_seen_${user.id}`, "1");
      }
    } catch { /* ignore */ }
    // eslint-disable-next-line
  }, [user]);

  useRealtimeEvents({
    message: (data) => {
      load();
      if (data?.from_role === "admin" && state !== "open") {
        setUnread((u) => u + 1);
        persist("open");
      }
    },
    message_read: () => load(),
    download_approved: () => load(),
    message_reaction: () => load(),
    message_edit: () => load(),
    message_delete: () => load(),
    profile_pic_changed: (data) => {
      try { window.__bumpAvatarCache?.(data?.user_id); } catch { /* noop */ }
      if (data?.user_id === user?.id) setSelfHasPic(true);
    },
    presence: () => {
      axios.get(`${API}/admin/public-info`, { headers: authHeader() })
        .then((r) => setAdminInfo(r.data)).catch(() => null);
    },
    admin_presence: (data) => {
      setAdminInfo((a) => a ? { ...a, online: !!data?.online } : a);
    },
    typing: (data) => {
      if (data?.from_role === "admin") {
        setAdminTyping(true);
        if (typingTimerRef.current) clearTimeout(typingTimerRef.current);
        typingTimerRef.current = setTimeout(() => setAdminTyping(false), 3000);
      }
    },
  });

  const pingTyping = () => {
    const now = Date.now();
    if (now - lastTypingPingRef.current < 2500) return;
    lastTypingPingRef.current = now;
    axios.post(`${API}/users/typing`, {}, { headers: authHeader() }).catch(() => null);
  };

  // When the chat is open, mark visible admin messages as read.
  useEffect(() => {
    if (state === "open" && messages.length > 0) {
      markAdminMessagesRead();
    }
    // eslint-disable-next-line
  }, [state, messages.length]);

  useEffect(() => {
    if (state === "open" && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, state]);

  if (!user || user.role === "admin") return null;
  if (location.pathname.startsWith("/lecturaonline") || location.pathname.startsWith("/admin")) return null;

  const send = async (e) => {
    e?.preventDefault?.();
    const body = draft.trim();
    if (!body || sending) return;
    setSending(true);
    try {
      await axios.post(`${API}/users/messages`, {
        body, reply_to: replyingTo?.id || null,
      }, { headers: authHeader() });
      setDraft("");
      setReplyingTo(null);
      await load();
    } catch { /* ignore */ }
    finally { setSending(false); }
  };

  // ---------- Minimized: bottom bar (can re-open) ----------
  if (state === "min") {
    return (
      <button
        data-testid="user-chat-min"
        onClick={() => persist("open")}
        className="fixed bottom-0 right-6 z-[80] w-64 bg-gradient-to-r from-[#d4af37] to-amber-600 text-black border-t-2 border-x-2 border-amber-300/60 rounded-t-2xl px-4 py-2.5 flex items-center justify-between hover:brightness-110 transition shadow-2xl shadow-amber-500/30"
      >
        <span className="text-sm font-bold flex items-center gap-2">
          <MessageCircle className="w-4 h-4" /> Cristopher
          <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
        </span>
        {unread > 0 && (
          <span data-testid="user-chat-min-badge" className="h-5 min-w-[20px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center animate-bounce">
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>
    );
  }

  // ---------- Open: full chat box ----------
  return (
    <div
      data-testid="user-chat-window"
      className="fixed bottom-0 right-6 z-[80] w-[22rem] max-w-[92vw] h-[28rem] max-h-[70vh] bg-gradient-to-b from-[#1a1a1a] to-[#0a0a0a] border-t-2 border-x-2 border-[#d4af37]/40 rounded-t-2xl shadow-2xl shadow-purple-500/20 flex flex-col"
    >
      <div className="flex items-center justify-between px-3 py-2 border-b border-white/10 bg-gradient-to-r from-[#d4af37]/20 to-amber-500/20">
        <div className="flex items-center gap-2">
          {adminInfo?.has_profile_pic ? (
            <Avatar userId={adminInfo.id} name={adminInfo.name} hasPicHint authHeader={authHeader} size={28} />
          ) : (
            <div className="w-7 h-7 rounded-full bg-gradient-to-br from-[#d4af37] to-amber-600 flex items-center justify-center text-black text-xs font-bold">CA</div>
          )}
          <div>
            <div className="text-white text-sm font-semibold">Cristopher (CrisAngel)</div>
            <div className={`text-[10px] ${adminInfo?.online ? "text-emerald-300" : "text-neutral-400"}`}>
              {adminInfo?.online ? "● En línea" : "● Desconectado"}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <a
            data-testid="user-chat-whatsapp"
            href={`https://wa.me/526622870805?text=${encodeURIComponent("Hola CrisAngel, te escribo desde la página.")}`}
            target="_blank" rel="noreferrer"
            className="h-7 w-7 rounded-full bg-emerald-500/20 hover:bg-emerald-500/30 flex items-center justify-center text-emerald-300"
            title="WhatsApp"
          >
            <svg viewBox="0 0 24 24" className="w-3.5 h-3.5 fill-current"><path d="M17.5 14.4l-1.6-.8c-.2-.1-.4-.1-.6.1l-.7.9c-.1.1-.3.2-.5.1-.7-.3-1.5-.7-2.2-1.4-.5-.5-1-1.2-1.3-1.9-.1-.2-.1-.4.1-.5l.6-.7c.1-.2.2-.4.1-.6L10 7.2c-.1-.2-.4-.4-.7-.4-.8 0-1.5.6-1.7 1.4-.2.9.1 1.9.7 2.9 1.1 2 3.5 4.4 5.5 5.5 1 .6 2 .8 2.9.7.8-.2 1.4-.9 1.4-1.7 0-.3-.1-.5-.3-.6l-.3-.6zM12 2C6.5 2 2 6.5 2 12c0 1.7.4 3.3 1.2 4.7L2 22l5.4-1.4c1.4.7 2.9 1.2 4.6 1.2 5.5 0 10-4.5 10-10S17.5 2 12 2z"/></svg>
          </a>
          <a
            data-testid="user-chat-call"
            href="tel:+526622870805"
            className="h-7 w-7 rounded-full bg-sky-500/20 hover:bg-sky-500/30 flex items-center justify-center text-sky-300"
            title="Llamar"
          ><Phone className="w-3.5 h-3.5" /></a>
          <button
            data-testid="user-chat-minimize"
            onClick={() => persist("min")}
            className="h-7 w-7 rounded hover:bg-white/10 flex items-center justify-center text-neutral-300"
            title="Minimizar (no se puede cerrar)"
          ><Minus className="w-4 h-4" /></button>
        </div>
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto px-3 py-3 space-y-2">
        {messages.length === 0 ? (
          <p className="text-xs text-neutral-500 italic text-center py-6">
            Aún no hay mensajes. ¡Escríbele a Cristopher!
          </p>
        ) : messages.map((m) => (
          <ChatMessage
            key={m.id}
            msg={m}
            isOwn={m.from_role === "user"}
            authHeader={authHeader}
            mediaEndpointPrefix="/users/messages"
            viewerRole="user"
            peerInfo={adminInfo ? { id: adminInfo.id, name: adminInfo.name, has_profile_pic: adminInfo.has_profile_pic } : null}
            selfInfo={{ id: user.id, name: user.name || user.username, has_profile_pic: selfHasPic }}
            allMessages={messages}
            onReply={(parent) => setReplyingTo(parent)}
          />
        ))}
      </div>

      {/* Typing indicator */}
      {adminTyping && (
        <div data-testid="admin-typing-indicator" className="px-3 py-1 text-[10px] text-neutral-400 italic flex items-center gap-1.5">
          <span className="flex gap-0.5">
            <span className="w-1 h-1 bg-[#d4af37] rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
            <span className="w-1 h-1 bg-[#d4af37] rounded-full animate-bounce" style={{ animationDelay: "120ms" }} />
            <span className="w-1 h-1 bg-[#d4af37] rounded-full animate-bounce" style={{ animationDelay: "240ms" }} />
          </span>
          CrisAngel está escribiendo...
        </div>
      )}

      {/* Reply preview bar */}
      {replyingTo && (
        <div data-testid="user-reply-preview" className="px-3 py-1.5 bg-[#d4af37]/10 border-t border-[#d4af37]/30 text-[10px] flex items-center gap-2">
          <CornerDownRight className="w-3 h-3 text-[#d4af37]" />
          <span className="text-neutral-400 italic flex-1 truncate">Respondiendo: {(replyingTo.body || "(media)").slice(0, 60)}</span>
          <button data-testid="user-reply-cancel" onClick={() => setReplyingTo(null)} className="text-neutral-400 hover:text-white"><XIcon className="w-3 h-3" /></button>
        </div>
      )}

      <form onSubmit={send} className="border-t border-white/10 p-2 flex gap-2 items-center">
        <input
          type="file"
          accept="image/*"
          data-testid="user-chat-image-input"
          className="hidden"
          ref={(el) => { window.__userChatImageInput = el; }}
          onChange={async (e) => {
            const f = e.target.files?.[0];
            e.target.value = "";
            if (!f) return;
            if (f.size > 6 * 1024 * 1024) return alert("Imagen muy grande (max 6MB)");
            await sendMedia({ blob: f, mimeType: f.type, kind: "image" });
          }}
        />
        <button
          type="button"
          data-testid="user-chat-image-btn"
          onClick={() => window.__userChatImageInput?.click()}
          className="h-9 w-9 rounded-full bg-black/40 border border-white/10 hover:border-[#d4af37]/50 text-neutral-300 hover:text-[#d4af37] flex items-center justify-center"
          title="Enviar imagen"
        ><Paperclip className="w-4 h-4" /></button>
        <EmojiButton onSelect={(e) => setDraft((d) => d + e)} />
        <StickerButton onSelect={(id) => sendSticker(id)} />
        <VoiceRecorder onComplete={(p) => sendMedia({ ...p, kind: "voice" })} disabled={sending} />
        <DictateButton onText={(t) => setDraft((d) => (d ? `${d} ${t}` : t))} />
        <input
          data-testid="user-chat-input"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="Escribe..."
          className="flex-1 bg-black/50 border border-white/10 rounded-full px-3 py-2 text-xs text-white placeholder:text-neutral-500 focus:outline-none focus:border-[#d4af37]/50"
        />
        <button
          data-testid="user-chat-send"
          type="submit"
          disabled={sending || !draft.trim()}
          className="h-9 w-9 rounded-full bg-gradient-to-br from-[#d4af37] to-amber-600 text-black flex items-center justify-center disabled:opacity-40"
        ><Send className="w-3.5 h-3.5" /></button>
      </form>
    </div>
  );
};
